import { Node, Mark } from '@tiptap/core';

// Extension for ordered lists
export const OrderedList = Node.create({
  name: 'orderedList',
  group: 'block list',
  content: 'listItem+',
  allowGapCursor: false,

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'ol',
        getAttrs: (element) => {
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['ol', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      toggleOrderedList:
        () =>
        ({ commands, state, dispatch }) => {
          // If we're in a span or text node, first convert to paragraph
          if (
            state.selection.$from.parent.type.name === 'text' ||
            state.selection.$from.parent.type.name === 'span'
          ) {
            // First set to paragraph, then toggle list
            const setParagraphResult = commands.setParagraph();

            if (setParagraphResult) {
              // Now try the list toggle
              const listResult = commands.toggleList(this.name, 'listItem');
              return listResult;
            }
          }
          return commands.toggleList(this.name, 'listItem');
        },
      setOrderedList:
        () =>
        ({ commands }) => {
          return commands.setList(this.name, 'listItem');
        },
      unsetOrderedList:
        () =>
        ({ commands }) => {
          return commands.unsetList(this.name, 'listItem');
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-7': () => this.editor.commands.toggleOrderedList(),
      Tab: () => {
        // Check if we're in this list type and nest the current item
        if (this.editor.isActive('orderedList')) {
          return this.editor.commands.command(({ tr, state }) => {
            const { selection } = state;
            const { $from } = selection;

            // Find the current list item
            let listItemDepth = null;
            for (let depth = $from.depth; depth >= 0; depth--) {
              const node = $from.node(depth);
              if (node.type.name === 'listItem') {
                listItemDepth = depth;
                break;
              }
            }

            if (listItemDepth !== null) {
              // Create a nested ordered list inside the current list item
              const nestedList = state.schema.nodes.orderedList.create(
                null,
                state.schema.nodes.listItem.create(
                  null,
                  state.schema.nodes.paragraph.create(),
                ),
              );

              // Insert the nested list at the end of the current list item
              const insertPos = $from.end(listItemDepth) + 1;
              tr.insert(insertPos, nestedList);

              // Move cursor to the nested list item
              const newPos = insertPos + 2; // +1 for list, +1 for listItem, +1 for paragraph
              tr.setSelection(
                state.selection.constructor.near(tr.doc.resolve(newPos + 1)),
              );

              return true;
            }

            return false;
          });
        }
        return false;
      },
      Enter: () => {
        // Check if we're in this list type
        if (this.editor.isActive('orderedList')) {
          // Check if current list item is empty - if so, exit the list
          const { state } = this.editor;
          const { selection } = state;
          const { $from } = selection;

          // Find the current list item and its position
          let currentListItem = null;
          let listItemDepth = null;
          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'listItem') {
              currentListItem = node;
              listItemDepth = depth;
              break;
            }
          }

          if (currentListItem && listItemDepth !== null) {
            // Check if the current list item is empty
            const listItemStart = $from.start(listItemDepth);
            const listItemEnd = $from.end(listItemDepth);
            const listItemContent = state.doc.textBetween(
              listItemStart,
              listItemEnd,
            );

            // If the list item is completely empty or only contains whitespace
            if (listItemContent.trim().length === 0) {
              // Use a more direct approach to exit the list
              return this.editor.commands.command(({ tr, state, dispatch }) => {
                const { selection } = state;
                const { $from } = selection;

                // Find the list item position
                let listItemPos = null;
                for (let depth = $from.depth; depth >= 0; depth--) {
                  if ($from.node(depth).type.name === 'listItem') {
                    listItemPos = $from.before(depth);
                    break;
                  }
                }

                if (listItemPos !== null) {
                  // Delete the empty list item
                  const listItemEnd = $from.after($from.depth - 1);
                  tr.delete(listItemPos, listItemEnd);

                  // Insert a paragraph at the position where the list item was
                  const paragraph = state.schema.nodes.paragraph.create();
                  tr.insert(listItemPos, paragraph);

                  // Move cursor to the new paragraph
                  tr.setSelection(
                    state.selection.constructor.near(
                      tr.doc.resolve(listItemPos + 1),
                    ),
                  );

                  return true;
                }

                return false;
              });
            }
          }
          // Split the current node and create a new list item
          return this.editor.commands.command(({ tr, state }) => {
            const { selection } = state;
            const { $from } = selection;

            // Insert a new list item at the current position
            const newListItem = state.schema.nodes.listItem.create(
              null,
              state.schema.nodes.paragraph.create(),
            );

            const insertPos = $from.after();
            tr.insert(insertPos, newListItem);

            // Move cursor to the new list item's paragraph
            const newPos = insertPos + 2; // +1 for listItem, +1 for paragraph
            tr.setSelection(
              state.selection.constructor.near(tr.doc.resolve(newPos)),
            );

            return true;
          });
        }
        return false;
      },
    };
  },
});

// Extension for unordered lists
export const UnorderedList = Node.create({
  name: 'unorderedList',
  group: 'block list',
  content: 'listItem+',
  allowGapCursor: false,

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'ul',
        getAttrs: (element) => {
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['ul', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      toggleBulletList:
        () =>
        ({ commands, state, dispatch }) => {
          // If we're in a span or text node, first convert to paragraph
          if (
            state.selection.$from.parent.type.name === 'text' ||
            state.selection.$from.parent.type.name === 'span'
          ) {
            // First set to paragraph, then toggle list
            const setParagraphResult = commands.setParagraph();

            if (setParagraphResult) {
              // Now try the list toggle
              const listResult = commands.toggleList(this.name, 'listItem');
              return listResult;
            }
          }

          return commands.toggleList(this.name, 'listItem');
        },
      setBulletList:
        () =>
        ({ commands }) => {
          return commands.setList(this.name, 'listItem');
        },
      unsetBulletList:
        () =>
        ({ commands }) => {
          return commands.unsetList(this.name, 'listItem');
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-8': () => this.editor.commands.toggleBulletList(),
      Tab: () => {
        // Check if we're in this list type and nest the current item
        if (this.editor.isActive('unorderedList')) {
          return this.editor.commands.command(({ tr, state }) => {
            const { selection } = state;
            const { $from } = selection;

            // Find the current list item
            let listItemDepth = null;
            for (let depth = $from.depth; depth >= 0; depth--) {
              const node = $from.node(depth);
              if (node.type.name === 'listItem') {
                listItemDepth = depth;
                break;
              }
            }

            if (listItemDepth !== null) {
              // Create a nested unordered list inside the current list item
              const nestedList = state.schema.nodes.unorderedList.create(
                null,
                state.schema.nodes.listItem.create(
                  null,
                  state.schema.nodes.paragraph.create(),
                ),
              );

              // Insert the nested list at the end of the current list item
              const insertPos = $from.end(listItemDepth) + 1;
              tr.insert(insertPos, nestedList);

              // Move cursor to the nested list item
              const newPos = insertPos + 2; // +1 for list, +1 for listItem, +1 for paragraph
              tr.setSelection(
                state.selection.constructor.near(tr.doc.resolve(newPos + 1)),
              );

              return true;
            }

            return false;
          });
        }
        return false;
      },
      Enter: () => {
        // Check if we're in this list type
        if (this.editor.isActive('unorderedList')) {
          // Check if current list item is empty - if so, exit the list
          const { state } = this.editor;
          const { selection } = state;
          const { $from } = selection;

          // Find the current list item and its position
          let currentListItem = null;
          let listItemDepth = null;
          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'listItem') {
              currentListItem = node;
              listItemDepth = depth;
              break;
            }
          }

          if (currentListItem && listItemDepth !== null) {
            // Check if the current list item is empty
            const listItemStart = $from.start(listItemDepth);
            const listItemEnd = $from.end(listItemDepth);
            const listItemContent = state.doc.textBetween(
              listItemStart,
              listItemEnd,
            );

            // If the list item is completely empty or only contains whitespace
            if (listItemContent.trim().length === 0) {
              // Use a more direct approach to exit the list
              return this.editor.commands.command(({ tr, state, dispatch }) => {
                const { selection } = state;
                const { $from } = selection;

                // Find the list item position
                let listItemPos = null;
                for (let depth = $from.depth; depth >= 0; depth--) {
                  if ($from.node(depth).type.name === 'listItem') {
                    listItemPos = $from.before(depth);
                    break;
                  }
                }

                if (listItemPos !== null) {
                  // Delete the empty list item
                  const listItemEnd = $from.after($from.depth - 1);
                  tr.delete(listItemPos, listItemEnd);

                  // Insert a paragraph at the position where the list item was
                  const paragraph = state.schema.nodes.paragraph.create();
                  tr.insert(listItemPos, paragraph);

                  // Move cursor to the new paragraph
                  tr.setSelection(
                    state.selection.constructor.near(
                      tr.doc.resolve(listItemPos + 1),
                    ),
                  );

                  return true;
                }

                return false;
              });
            }
          }
          // Split the current node and create a new list item
          return this.editor.commands.command(({ tr, state }) => {
            const { selection } = state;
            const { $from } = selection;

            // Insert a new list item at the current position
            const newListItem = state.schema.nodes.listItem.create(
              null,
              state.schema.nodes.paragraph.create(),
            );

            const insertPos = $from.after();
            tr.insert(insertPos, newListItem);

            // Move cursor to the new list item's paragraph
            const newPos = insertPos + 2; // +1 for listItem, +1 for paragraph
            tr.setSelection(
              state.selection.constructor.near(tr.doc.resolve(newPos)),
            );

            return true;
          });
        }
        return false;
      },
    };
  },
});

// Extension for list items
export const ListItem = Node.create({
  name: 'listItem',
  group: 'list',
  content: 'paragraph block* (orderedList | unorderedList)*',
  defining: true,

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'li',
        getAttrs: (element) => {
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['li', HTMLAttributes, 0];
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // Indent list item by creating a nested list
        return this.editor.commands.command(({ tr, state }) => {
          const { selection } = state;
          const { $from } = selection;

          // Check if we're in a list item and find the parent list type
          let listItemPos = null;
          let parentListType = 'unorderedList'; // default

          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'listItem') {
              listItemPos = $from.start(depth);
              // Check the parent list type
              if (depth > 0) {
                const parentNode = $from.node(depth - 1);
                if (parentNode.type.name === 'orderedList') {
                  parentListType = 'orderedList';
                } else if (parentNode.type.name === 'unorderedList') {
                  parentListType = 'unorderedList';
                }
              }
              break;
            }
          }

          if (listItemPos !== null) {
            // Create a nested list of the same type as the parent
            const nestedList = state.schema.nodes[parentListType].create(
              null,
              state.schema.nodes.listItem.create(
                null,
                state.schema.nodes.paragraph.create(),
              ),
            );

            // Insert the nested list at the end of the current list item
            tr.insert($from.end() + 1, nestedList);
            return true;
          }

          return false;
        });
      },
      'Shift-Tab': () => {
        // Outdent list item (lift it to parent level)
        return this.editor.commands.command(({ tr, state }) => {
          const { selection } = state;
          const { $from } = selection;

          // Check if we're in a nested list structure
          let nestedListDepth = null;
          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth);
            if (
              node.type.name === 'unorderedList' ||
              node.type.name === 'orderedList'
            ) {
              // Check if this list is nested (has a parent list)
              if (depth > 0) {
                const parentNode = $from.node(depth - 1);
                if (parentNode.type.name === 'listItem') {
                  nestedListDepth = depth;
                  break;
                }
              }
            }
          }

          if (nestedListDepth !== null) {
            // Move the current list item to the parent list level
            const currentListItem = $from.node(nestedListDepth + 1);
            const parentListPos = $from.start(nestedListDepth - 1);

            // Remove from nested list and add to parent list
            tr.delete(
              $from.start(nestedListDepth + 1),
              $from.end(nestedListDepth + 1) + 1,
            );
            tr.insert(
              parentListPos + $from.node(nestedListDepth - 1).content.size,
              currentListItem,
            );
            return true;
          }

          return false;
        });
      },
    };
  },
});

// Extension for strong/bold text
export const Strong = Mark.create({
  name: 'strong',

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'strong',
        getAttrs: (element) => {
          return {};
        },
      },
      {
        tag: 'b',
        getAttrs: (element) => {
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['strong', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      toggleBold:
        () =>
        ({ commands }) => {
          return commands.toggleMark(this.name);
        },
      setBold:
        () =>
        ({ commands }) => {
          return commands.setMark(this.name);
        },
      unsetBold:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});

// Extension for emphasis/italic text
export const Emphasis = Mark.create({
  name: 'emphasis',

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'em',
        getAttrs: (element) => {
          return {};
        },
      },
      {
        tag: 'i',
        getAttrs: (element) => {
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['em', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      toggleItalic:
        () =>
        ({ commands }) => {
          return commands.toggleMark(this.name);
        },
      setItalic:
        () =>
        ({ commands }) => {
          return commands.setMark(this.name);
        },
      unsetItalic:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
